import { useState, useEffect, useMemo } from 'react';
import { useTable } from 'react-table';
import _, { debounce } from 'lodash';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';

import { withAuthorization } from '../Session';
import useStyles from './Products.styles';
import Layout from '../Page';
import PageHeader from './pageHeader';
import PageToolbar from './pageToolbar';
import { FilterProvider } from '../../context/filterContext';
import { deleteProducts, exportProducts } from '../../db/products';
import { getTemplates } from '../../db/templates';
import { getTags } from '../../db/tags';
import ProductModal from './productModal';
import UploadModal from './uploadModal';
import UploadProductModal from './uploadProductModal';
import ProductGridItem from './productGridItem';
import { searchProducts } from '../../module/typesense';
import { filterShows, _filterTemplates } from './Products.constants';
import { sortSizes } from '../../utils/sortSizes';
import { ConfirmDialog } from '../Page/generalModal';
import { Toast } from '../Toast';

const DEFAULT_OPTIONS = [{ id: 1, name: 'All', val: '' }];

const appendIndexes = (items) =>
  // + 2 because "ALL is index 1"
  items.map((item, index) => ({
    ...item,
    id: index + 2,
    styles: item?.styles?.map((style, index) => ({
      ...style,
      id: index + 2,
      name: style?.styleName,
      formats: style?.formats?.map((format, index) => ({
        ...format,
        id: index + 2,
        sizes: format?.sizes?.map((size, index) => ({
          ...size,
          id: index + 2,
        })),
      })),
    })),
  }));

// component module
const Products = (props) => {
  const [selected, setSelected] = useState('');
  const [filterType, setFilterType] = useState(1);
  const [filterShow, setFilterShow] = useState(5);
  const [filterStyle, setFilterStyle] = useState(1);
  const [filterFormat, setFilterFormat] = useState(1);
  const [filterSize, setFilterSize] = useState(1);
  const [filterTemplate, setFilterTemplate] = useState('All');

  const classes = useStyles();
  const db = props.firebase.getdb();
  const storage = props.firebase.getstorage();
  const [data, setData] = useState([]);
  const [productCount, setProductCount] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [formats, setFormats] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [tags, setTags] = useState([]);
  const [cropSettings, setCropSettings] = useState([]);
  const [filterTemplates, setFilterTemplates] = useState([]);
  const [filterFormats, setFilterFormats] = useState([]);
  const [filterSizes, setFilterSizes] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [textSearch, setTextSearch] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openBulkModal, setOpenBulkModal] = useState(false);
  const [openUploadProductModal, setOpenUploadProductModal] = useState(false);

  const [updates, setUpdates] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [filtersPushed, setFiltersPushed] = useState([]);

  const [options, setOptions] = useState({
    textSearch: textSearch,
    rowsPerPage: rowsPerPage,
    pageNumber: pageNumber,
    filters: '',
  });

  const [availableTypes, setAvailableTypes] = useState([]);
  const [availableStyles] = useState([]);

  useEffect(() => {
    const _availableTypes = [...DEFAULT_OPTIONS, ...appendIndexes(props.types)];
    setAvailableTypes(_availableTypes);
  }, [props.types]);

  // define table columns
  const columns = useMemo(
    () => [
      {
        Header: 'SKU',
        accessor: 'sku',
      },
      {
        Header: 'Name',
        accessor: 'name',
      },
      {
        Header: 'Product',
        accessor: 'type',
      },
      {
        Header: 'Style',
        accessor: 'style',
      },
      {
        Header: 'Format',
        accessor: 'format',
      },
      {
        Header: 'Size',
        accessor: 'size',
      },
    ],
    [],
  );

  // Use the useTable Hook to send the columns and data to build the table
  const {
    getTableProps, // table props from react-table
    getTableBodyProps, // table body props from react-table
    headerGroups, // headerGroups, if your table has groupings
    rows, // rows for the table based on the data passed
    prepareRow, // Prepare the row (this function needs to be called for each row before getting the row props)
  } = useTable({
    columns,
    data,
  });

  useEffect(() => {
    getTemplates(db, 150, fetchTemplatesData);
    getTags(db, 100, fetchTagsData);
  }, []);

  useEffect(() => {
    fetchFormatData(props.formats);
  }, [props.formats]);

  useEffect(() => {
    // setSizes([...props.sizes]);
    fetchSizeData(props.sizes);
  }, [props.sizes]);

  useEffect(() => {
    fetchCropSettings(props.cropSettings);
  }, [props.cropSettings]);

  const fetchTemplatesData = (items) => {
    const _templates = _filterTemplates.concat(items);
    setFilterTemplates([..._templates]);

    setTemplates([...items]);
  };

  const fetchTagsData = (items) => {
    setTags([...items]);
  };

  const fetchCropSettings = (items) => {
    setCropSettings([...items]);
  };

  const fetchFormatData = (items) => {
    const _filterFormats = [
      { id: 1, name: 'All', val: '' },
      { id: 2, name: 'None', val: '' },
    ];

    const formats = _.map(items, (item, index) => ({
      id: index + 3,
      name: item.name,
      val: item.id,
    }));
    const _formats = _filterFormats.concat(formats);
    setFilterFormats([..._formats]);
    setFormats([...items]);
  };

  const fetchSizeData = (items) => {
    const sortedSizes = sortSizes(items);

    const _filterFormats = [
      { id: 2, name: 'None', val: '' },
      { id: 1, name: 'All', val: '' },
    ];

    const formats = _.map(sortedSizes, (item, index) => ({
      id: index + 3,
      name: item.name,
      val: item.id,
    }));

    const _formats = _filterFormats.concat(formats);
    setFilterSizes([..._formats]);
    setSizes([...items]);
  };

  const debouncedSearch = debounce(() => {
    searchProducts(options, fetchData, true, db, sizes, formats, true);
  }, 1000);

  useEffect(() => {
    debouncedSearch();
  }, [options]);

  // update options
  useEffect(() => {
    const oldFilters = options.filters;

    const filterArray = [];

    filterArray.push('isBoxset:=false');

    filterArray.push(...filtersPushed);

    // if filters changed, reset page back to 1
    const newFilters = filterArray.join(' && ');

    setOptions({
      textSearch: textSearch,
      rowsPerPage: rowsPerPage,
      pageNumber: oldFilters === newFilters ? pageNumber : 1,
      filters: newFilters,
    });
  }, [pageNumber, textSearch, filtersPushed, filterTemplate, rowsPerPage]);

  // callback for getProducts
  const fetchData = (products, productsMetadata) => {
    setData([...products]);
    setProductCount(productsMetadata.productCount);
    setTotalPages(productsMetadata.pages);
  };

  const handleSearch = (event) => {
    // if user presses enter or clears the box, process the search
    if (event.keyCode === 13 || event.target.value.length === 0) {
      setPageNumber(1);
      setTextSearch(event.target.value);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPageNumber(newPage);
  };

  const handleChangeShow = (id) => {
    // use this to bind the drop-down
    setFilterShow(id);

    // need to get the value to update Algolia
    const selectedObject = _.find(filterShows, (item) => item.id === id);
    setRowsPerPage(selectedObject.name);
  };

  const handleClick = (event, name) => {
    if (props.viewOnly || props.editOnly) {
      setSelected(name);
      setOpenModal(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenModal(false);

    setTimeout(() => {
      setUpdates(updates + 1);
    }, 5000);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateModal(false);
    setTimeout(() => {
      setUpdates(updates + 1);
    }, 5000);
  };

  const handleCloseBulkDialog = () => {
    setOpenBulkModal(false);

    setTimeout(() => {
      setUpdates(updates + 1);
    }, 5000);
  };

  const handleCloseUploadArtDialog = () => {
    setOpenUploadProductModal(false);
  };

  const handleAddProduct = () => {
    setOpenCreateModal(true);
  };

  const handleUpload = () => {
    setOpenBulkModal(true);
  };

  const handleUploadArt = () => {
    console.log('handleUploadArt');
    setOpenUploadProductModal(true);
  };

  const handleExport = () => {
    if (templates.length > 0) {
      const type = _.find(availableTypes, { id: filterType }).name;
      const style = _.find(availableStyles, { id: filterStyle }).name;
      const format = _.find(filterFormats, { id: filterFormat }).val;
      const size = _.find(filterSizes, { id: filterSize }).val;
      setExporting(true);
      exportProducts(
        db,
        { type, style, format, size, template: filterTemplate },
        sizes,
        formats,
        templates,
        handleCompleteExport,
      );
    }
  };

  const handleCompleteExport = () => {
    setExporting(false);
  };

  const onChangeView = (mode) => {
    console.log('Change View: ', mode);
    setViewMode(mode);
  };

  const [selectedRows, setSelectedRows] = useState([]);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastData, setToastData] = useState({});

  // Handle selecting/deselecting all rows
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(rows.map((row) => row.values.sku)); // Select all SKUs
    } else {
      setSelectedRows([]); // Deselect all
    }
  };

  // Handle selecting/deselecting a single row
  const handleSelectRow = (sku) => {
    setSelectedRows(
      (prevSelected) =>
        prevSelected.includes(sku)
          ? prevSelected.filter((id) => id !== sku) // Deselect
          : [...prevSelected, sku], // Select
    );
  };

  // list of items to render in PRODUCTS PAGE
  const isAllSelected = rows.length > 0 && selectedRows.length === rows.length;

  const handleDeleteAction = () => {
    console.log('handleDeleteAction');
    setOpenRemoveModal(true);
  };

  const handleBulkDelete = async () => {
    console.log('handleBulkDelete');

    if (selectedRows.length === 0) {
      return;
    }

    try {
      await deleteProducts(db, selectedRows);
      // Remove deleted items from data
      const updatedData = data.filter(
        (item) => !selectedRows.includes(item.id),
      );

      setData(updatedData); // Update state with filtered data
      setSelectedRows([]); // Clear selection after deletion
      setToastData({
        type: 'success',
        message: 'Products deleted successfully',
      });
      setShowToast(true);
    } catch (error) {
      console.error('Bulk delete failed', error);
      setToastData({
        type: 'error',
        message: 'Bulk delete failed',
      });
      setShowToast(true);
    }

    setOpenRemoveModal(false);
  };

  const renderListView = () => (
    <TableContainer sx={{ marginTop: '30px', marginBottom: '50px' }}>
      <Table className={classes.table} size="medium" {...getTableProps()}>
        <TableHead className={classes.tableHead}>
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {/* Select All Checkbox */}
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  indeterminate={
                    selectedRows.length > 0 && selectedRows.length < rows.length
                  }
                />
              </TableCell>
              {headerGroup.headers.map((column) => (
                <TableCell
                  {...column.getHeaderProps()}
                  className={classes.tableHeadCell}
                >
                  <Typography variant="tableText">
                    {column.render('Header')}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>

        <TableBody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <TableRow
                hover
                {...row.getRowProps()}
                onClick={(event) => handleClick(event, row.values.sku)}
                selected={selectedRows.includes(row.values.sku)}
                classes={{
                  root: i % 2 === 1 ? classes.tableRow : classes.tableRow2,
                  selected: classes.selectedTableRow,
                }}
              >
                {/* Checkbox for row selection */}
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedRows.includes(row.values.sku)}
                    onChange={() => handleSelectRow(row.values.sku)}
                    onClick={(e) => e.stopPropagation()} // Prevent triggering row click
                  />
                </TableCell>
                {row.cells.map((cell) => (
                  <TableCell {...cell.getCellProps()}>
                    <Typography variant="tableText">
                      {cell.render('Cell')}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderGridView = () => (
    <Box>
      <Grid container spacing={3}>
        {rows.map((row) => (
          <Grid item xs={3}>
            <ProductGridItem
              key={`GridItem_${row.values.sku}`}
              db={db}
              data={row.values}
              settings={cropSettings}
              templates={templates}
              handleClick={handleClick}
              storage={storage}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderProduct = () =>
    viewMode === 'list' ? renderListView() : renderGridView();

  return (
    <Layout page={props.page} user={props.user}>
      <PageHeader
        viewOnly={props.viewOnly}
        editOnly={props.editOnly}
        initial={productCount}
        onSearch={handleSearch}
        onUpload={handleUpload}
        onUploadArt={handleUploadArt}
        onExport={handleExport}
        exporting={exporting}
        db={db}
      />

      {templates.length > 0 && filterFormats.length > 0 && (
        // state management for filter context
        <FilterProvider
          value={{
            types: availableTypes,
            styles: availableStyles,
            formats: filterFormats,
            sizes: filterSizes,
            templates: filterTemplates,
            shows: filterShows,
            filterType,
            setFilterType,
            filterShow,
            setFilterShow: handleChangeShow,
            filterStyle,
            setFilterStyle,
            filterFormat,
            setFilterFormat,
            filterSize,
            setFilterSize,
            filterTemplate,
            setFilterTemplate,
          }}
        >
          <PageToolbar
            numSelected={0}
            viewOnly={props.viewOnly}
            editOnly={props.editOnly}
            handleNew={handleAddProduct}
            view={viewMode}
            onChangeView={onChangeView}
            sizes={props.sizes}
            formats={props.formats}
            setFiltersPushed={setFiltersPushed}
            filtersPushed={filtersPushed}
            selectedRows={selectedRows}
            handleDeleteAction={handleDeleteAction}
          />
        </FilterProvider>
      )}

      {templates.length > 0 && cropSettings.length > 0 && renderProduct()}

      <div className={classes.pagination}>
        <Pagination
          count={totalPages}
          page={pageNumber}
          onChange={handleChangePage}
          showFirstButton
          showLastButton
        />
      </div>

      {selected &&
        templates.length > 0 &&
        tags.length > 0 &&
        cropSettings.length > 0 &&
        formats.length > 0 && (
          <ProductModal
            {...props}
            key="Product_Modal_Edit"
            isCreate={false}
            viewOnly={props.viewProduct}
            editOnly={props.editProduct && (props.editOnly || props.viewOnly)}
            open={openModal}
            addingProduct={false}
            formats={formats}
            templates={templates}
            tags={tags}
            cropSettings={cropSettings}
            handleClose={handleCloseDialog}
            sku={selected}
          />
        )}

      {templates.length > 0 &&
        tags.length > 0 &&
        cropSettings.length > 0 &&
        formats.length > 0 && (
          // ! for add product
          <ProductModal
            {...props}
            key="Product_Modal_Create"
            isCreate={true}
            viewOnly={props.viewProduct}
            editOnly={props.editProduct}
            open={openCreateModal}
            addingProduct={true}
            formats={formats}
            templates={templates}
            tags={tags}
            cropSettings={cropSettings}
            handleClose={handleCloseCreateDialog}
            sku=""
          />
        )}

      {templates.length > 0 && formats.length > 0 && (
        <UploadModal
          key="Upload_Modal1"
          open={openBulkModal}
          templates={templates}
          formats={formats}
          onClose={handleCloseBulkDialog}
        />
      )}

      {templates.length > 0 &&
        cropSettings.length > 0 &&
        tags.length > 0 &&
        formats.length > 0 && (
          <UploadProductModal
            key="Upload_Modal2"
            open={openUploadProductModal}
            templates={templates}
            formats={formats}
            sizes={sizes}
            tags={tags}
            cropSettings={cropSettings}
            onClose={handleCloseUploadArtDialog}
          />
        )}
      {openRemoveModal && (
        <ConfirmDialog
          label="delete-dialog-title"
          title="Delete Product"
          open={openRemoveModal}
          onConfirm={handleBulkDelete}
          onClose={() => setOpenRemoveModal(false)}
        >
          <Typography variant="body2" gutterBottom>
            Do you want to remove:
          </Typography>
          <Typography variant="body2" component="div">
            <strong>
              {selectedRows.map((sku) => (
                <div key={sku}>{sku}</div>
              ))}
            </strong>
          </Typography>
        </ConfirmDialog>
      )}
      <Toast
        show={showToast}
        data={toastData}
        onClose={() => setShowToast(false)}
      />
    </Layout>
  );
};

const condition = (authUser) => !!authUser;

export default withAuthorization(condition)(Products);
